/** Backup gzip de uma lista local-first para `POST /api/v2/lists/:id/leads`. */
import {
  enhancedAwaitsReceipt,
  getList,
  getPage,
  lookalikeAwaitsReceipt,
  recordMatchesListModule,
  markListSynced,
  patchListBackup,
  type LocalLead,
  type LocalList,
  type LocalPlace,
} from '../data/localDb';
import {
  postListEnhanced,
  postListLookalike,
  type EnhancedListResponse,
  type EnhancedPlaceSource,
  type LookalikeListResponse,
} from '../api/localListMarks';
import type { ApiResult } from '../types/api';
import { apiRequest } from './apiClient';

/** P4: 4.000 Places completos = 8.650.049 B bruto / 1.171.208 B gzip; 10 mil = 3 calls. */
export const BACKUP_CHUNK_RECORDS = 4000;
export const BACKUP_MAX_ATTEMPTS = 5;
/** Esperas depois das falhas 1–4; a quinta falha exige tentativa manual. */
export const BACKUP_BACKOFF_MS = [5_000, 30_000, 2 * 60_000, 10 * 60_000] as const;

type BackupRecord = LocalLead | LocalPlace;
type WireRecord = Omit<LocalLead, 'listId'> | Omit<LocalPlace, 'listId'>;

export type BackupChunkPayload = {
  chunk: number;
  chunks: number;
  batchId: string;
} & ({ leads: WireRecord[] } | { places: WireRecord[] });

export interface BackupChunkReceipt {
  chunk: number;
  received: number;
  inserted: number;
  total: number;
}

export type ListBackupResult =
  | { status: 'skipped' }
  | { status: 'synced' | 'pending'; chunks: number; chunkSize: number }
  | { status: 'retry'; error: string; chunkSize: number; nextAttemptAt: string }
  | { status: 'error'; error: string; chunkSize: number };

interface BackupStorage {
  getList: typeof getList;
  getPage: typeof getPage;
  patchListBackup: typeof patchListBackup;
  markListSynced: typeof markListSynced;
}

interface BackupDependencies {
  storage: BackupStorage;
  sendChunk: (listId: string, payload: BackupChunkPayload) => Promise<ApiResult<BackupChunkReceipt>>;
  sendEnhanced: (
    listId: string,
    places: readonly EnhancedPlaceSource[],
  ) => Promise<EnhancedListResponse>;
  sendLookalike: (listId: string, resolvedThrough?: string) => Promise<LookalikeListResponse>;
  now: () => string;
}

export interface UploadListBackupOptions {
  /** Injetável para testar a redução sem alocar 4.000 registros. */
  initialChunkSize?: number;
  dependencies?: BackupDependencies;
}

const DEFAULT_STORAGE: BackupStorage = { getList, getPage, patchListBackup, markListSynced };

function split<T>(records: readonly T[], size: number): T[][] {
  if (records.length === 0) return [[]];
  const chunks: T[][] = [];
  for (let offset = 0; offset < records.length; offset += size) {
    chunks.push(records.slice(offset, offset + size));
  }
  return chunks;
}

function withoutListId(record: BackupRecord): WireRecord {
  const { listId: _listId, ...wire } = record;
  return wire;
}

function payloadFor(
  list: LocalList,
  batchId: string,
  chunk: number,
  chunks: number,
  records: readonly BackupRecord[],
): BackupChunkPayload {
  const wire = records.map(withoutListId);
  const envelope = { chunk, chunks, batchId };
  return list.module === 'places'
    ? { ...envelope, places: wire }
    : { ...envelope, leads: wire };
}

export function batchIdFor(listId: string, dirtyAt: string, chunkSize: number): string {
  // Determinístico: a retomada reutiliza a mesma identidade e os recibos persistidos na lista.
  // Um 413 muda o tamanho e, portanto, inicia uma remessa coerente nova.
  return `local-${listId}-${dirtyAt}-${chunkSize}`.slice(0, 200);
}

function positiveInteger(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0;
}

function initialChunkSize(
  list: LocalList,
  expectedDirtyAt: string,
  requested: number | undefined,
): number {
  if (requested !== undefined) return Math.max(1, Math.trunc(requested));
  if (
    positiveInteger(list.backupChunkSize) &&
    list.backupBatchId === batchIdFor(list.id, expectedDirtyAt, list.backupChunkSize)
  ) {
    return list.backupChunkSize;
  }
  return BACKUP_CHUNK_RECORDS;
}

function confirmedChunks(list: LocalList, batchId: string, chunks: number): Set<number> {
  if (list.backupBatchId !== batchId || !Array.isArray(list.backupConfirmedChunks)) {
    return new Set();
  }
  return new Set(
    list.backupConfirmedChunks.filter(
      (chunk) => Number.isInteger(chunk) && chunk >= 0 && chunk < chunks,
    ),
  );
}

function retryAt(now: string, attempts: number): string {
  const parsed = Date.parse(now);
  const base = Number.isFinite(parsed) ? parsed : Date.now();
  const delay = BACKUP_BACKOFF_MS[Math.max(0, attempts - 1)] ?? BACKUP_BACKOFF_MS.at(-1)!;
  return new Date(base + delay).toISOString();
}

function validReceipt(value: unknown, expectedChunk: number): value is BackupChunkReceipt {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const receipt = value as Record<string, unknown>;
  return (
    receipt['chunk'] === expectedChunk &&
    Number.isInteger(receipt['received']) &&
    Number.isInteger(receipt['inserted']) &&
    Number.isInteger(receipt['total'])
  );
}

/** Gzip portátil: ReadableStream manual, sem `blob.stream()` (indisponível no jsdom). */
export async function gzipJson(value: unknown): Promise<ArrayBuffer> {
  if (typeof CompressionStream === 'undefined') {
    throw new Error('compression_unavailable');
  }

  const encoded = new TextEncoder().encode(JSON.stringify(value));
  const source = new ReadableStream<BufferSource>({
    start(controller) {
      controller.enqueue(encoded);
      controller.close();
    },
  });
  const reader = source.pipeThrough(new CompressionStream('gzip')).getReader();
  const chunks: Uint8Array[] = [];
  let length = 0;
  let reading = true;
  while (reading) {
    const { done, value: chunk } = await reader.read();
    if (done) {
      reading = false;
      continue;
    }
    chunks.push(chunk);
    length += chunk.byteLength;
  }

  const output = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return output.buffer;
}

export async function postBackupChunk(
  listId: string,
  payload: BackupChunkPayload,
): Promise<ApiResult<BackupChunkReceipt>> {
  const rawBody = await gzipJson(payload);
  return apiRequest<BackupChunkReceipt>(
    `api/v2/lists/${encodeURIComponent(listId)}/leads`,
    {
      method: 'POST',
      rawBody,
      headers: {
        'Content-Type': 'application/json',
        'Content-Encoding': 'gzip',
      },
    },
  );
}

const DEFAULT_DEPENDENCIES: BackupDependencies = {
  storage: DEFAULT_STORAGE,
  sendChunk: postBackupChunk,
  sendEnhanced: postListEnhanced,
  sendLookalike: postListLookalike,
  now: () => new Date().toISOString(),
};

export interface PendingListBackupMarkers {
  enrichedAt?: string;
  lookalikeAt?: string;
  lookalikeSnapshot?: string;
}

/** O recibo guarda exatamente a versão da marca confirmada; diferença significa trabalho. */
export function pendingListBackupMarkers(list: LocalList): PendingListBackupMarkers {
  return {
    ...(enhancedAwaitsReceipt(list) ? { enrichedAt: list.enrichedAt } : {}),
    ...(lookalikeAwaitsReceipt(list)
      ? {
          lookalikeAt: list.lookalikeAt,
          ...(list.lookalikeSnapshot ? { lookalikeSnapshot: list.lookalikeSnapshot } : {}),
        }
      : {}),
  };
}

export function hasPendingListBackupMarkers(list: LocalList): boolean {
  const markers = pendingListBackupMarkers(list);
  return Boolean(markers.enrichedAt || markers.lookalikeAt);
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

/**
 * Sobe o snapshot observado no início. Só limpa `dirtyAt` se ele continuar igual depois de todos
 * os ACKs; escrita concorrente permanece `local` para a próxima rodada (invariante I4).
 */
export async function uploadListBackup(
  listId: string,
  options: UploadListBackupOptions = {},
): Promise<ListBackupResult> {
  const dependencies = options.dependencies ?? DEFAULT_DEPENDENCIES;
  const list = await dependencies.storage.getList(listId);
  if (!list) return { status: 'skipped' };

  const expectedDirtyAt = list.dirtyAt;
  const pendingMarkers = pendingListBackupMarkers(list);
  if (!expectedDirtyAt && !pendingMarkers.enrichedAt && !pendingMarkers.lookalikeAt) {
    return { status: 'skipped' };
  }
  const attempts = (list.backupAttempts ?? 0) + 1;
  let chunkSize = expectedDirtyAt
    ? initialChunkSize(list, expectedDirtyAt, options.initialChunkSize)
    : Math.max(1, Math.trunc(options.initialChunkSize ?? BACKUP_CHUNK_RECORDS));

  try {
    if (!(await dependencies.storage.patchListBackup(listId, {
      backupState: 'sending',
      backupAttempts: attempts,
    }))) {
      return { status: 'skipped' };
    }

    let records: BackupRecord[] | undefined;
    const readRecords = async (): Promise<BackupRecord[]> => {
      if (records) return records;
      const page = list.module === 'places'
        ? await dependencies.storage.getPage<LocalPlace>('places', listId)
        : await dependencies.storage.getPage<LocalLead>(list.module, listId);
      // Repara defensivamente registros cruzados que versões anteriores de semelhantes possam
      // ter gravado (telefone em emails ou email em phones). Perfis continuam no snapshot.
      records = (page.records as BackupRecord[]).filter((record) =>
        recordMatchesListModule(record, list.module));
      return records;
    };

    let chunks = 0;
    if (expectedDirtyAt) {
      const snapshot = await readRecords();
      let uploading = true;
      while (uploading) {
        const groups = split(snapshot, chunkSize);
        chunks = groups.length;
        const batchId = batchIdFor(listId, expectedDirtyAt, chunkSize);
        const resumed = confirmedChunks(list, batchId, groups.length);
        const confirmed = batchId === list.backupBatchId
          ? resumed
          : new Set<number>();
        let resize = false;

        if (
          list.backupBatchId !== batchId ||
          list.backupChunkSize !== chunkSize ||
          confirmed.size !== (list.backupConfirmedChunks?.length ?? 0)
        ) {
          if (!(await dependencies.storage.patchListBackup(listId, {
            backupBatchId: batchId,
            backupChunkSize: chunkSize,
            backupConfirmedChunks: [...confirmed].sort((left, right) => left - right),
          }))) {
            return { status: 'skipped' };
          }
        }

        for (let chunk = 0; chunk < groups.length; chunk += 1) {
          if (confirmed.has(chunk)) continue;
          const payload = payloadFor(list, batchId, chunk, groups.length, groups[chunk] ?? []);
          const response = await dependencies.sendChunk(listId, payload);
          if (!response.ok) {
            if (response.code === 'too_large' && chunkSize > 1) {
              resize = true;
              break;
            }
            throw new Error(response.code);
          }
          if (!validReceipt(response.data, chunk)) throw new Error('invalid_backup_receipt');
          confirmed.add(chunk);
          if (!(await dependencies.storage.patchListBackup(listId, {
            backupBatchId: batchId,
            backupChunkSize: chunkSize,
            backupConfirmedChunks: [...confirmed].sort((left, right) => left - right),
          }))) {
            return { status: 'skipped' };
          }
        }

        if (resize) {
          chunkSize = Math.max(1, Math.floor(chunkSize / 2));
          list.backupBatchId = undefined;
          list.backupChunkSize = undefined;
          list.backupConfirmedChunks = undefined;
          continue;
        }

        uploading = false;
      }
    }

    const confirmedMarkers: PendingListBackupMarkers = {};
    if (pendingMarkers.enrichedAt) {
      const places = (await readRecords())
        .filter((record): record is LocalPlace => 'cid' in record)
        .filter((place) => Boolean(place.enhancedAt));
      const response = await dependencies.sendEnhanced(listId, places);
      if (response.enhanced !== true || !Number.isInteger(response.updated)) {
        throw new Error('invalid_enhanced_receipt');
      }
      confirmedMarkers.enrichedAt = pendingMarkers.enrichedAt;
    }
    if (pendingMarkers.lookalikeAt) {
      const response = await dependencies.sendLookalike(
        listId,
        pendingMarkers.lookalikeSnapshot,
      );
      if (response.lookalikeRevealed !== true) throw new Error('invalid_lookalike_receipt');
      confirmedMarkers.lookalikeAt = pendingMarkers.lookalikeAt;
    }

    const backupAt = dependencies.now();
    const synced = await dependencies.storage.markListSynced(listId, {
      expectedDirtyAt,
      backupAt,
      confirmedMarkers,
    });
    return { status: synced ? 'synced' : 'pending', chunks, chunkSize };
  } catch (error) {
    const message = errorMessage(error);
    const terminal = attempts >= BACKUP_MAX_ATTEMPTS;
    const nextAttemptAt = terminal ? undefined : retryAt(dependencies.now(), attempts);
    const patched = await dependencies.storage.patchListBackup(listId, {
      backupState: terminal ? 'error' : 'local',
      backupError: message,
      backupAttempts: attempts,
      backupNextAttemptAt: nextAttemptAt,
    });
    if (!patched) return { status: 'skipped' };
    return terminal
      ? { status: 'error', error: message, chunkSize }
      : { status: 'retry', error: message, chunkSize, nextAttemptAt: nextAttemptAt! };
  }
}
